/* eslint-disable @next/next/no-img-element */

import {
  Content,
  ContentCard as ContentCardSlot,
} from '@yleisradio/areena-types';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import {
  useTrackCardClick,
  useViewUuid,
} from 'components/SnowplowEngagementAnalytics/hooks';
import { useBrazeSdk } from 'contexts/BrazeSdkContext';
import { BrazeContentCard as BrazeContentCardType } from 'contexts/BrazeSdkContext/types';
import { trackContentCardView } from 'services/snowplowAnalytics/snowplowAnalytics';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './BrazeContentCard.module.scss';
import { Button } from 'components/Button';
import { useEffect, useRef, useState } from 'react';

type Props = {
  card: BrazeContentCardType;
};

export const BrazeContentCard: React.FunctionComponent<Props> = ({ card }) => {
  const [isSent, setSent] = useState(false);
  const { braze } = useBrazeSdk();
  const rootRef = useRef<HTMLDivElement>(null);
  const viewUuid = useViewUuid();
  const { trackCardClick } = useTrackCardClick(viewUuid);

  useEffect(() => {
    if (braze && rootRef.current) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          braze.logContentCardImpressions([card]);

          const targetUri =
            card instanceof braze.ClassicCard ||
            card instanceof braze.CaptionedImage
              ? card.url
              : undefined;

          if (card.id && targetUri && !isSent) {
            trackContentCardView({
              viewUuid,
              contentCardId: card.id,
              placementId: getContentCardPlacementId(card),
              targetUri,
            });
            setSent(true);
          }

          observer.disconnect();
        }
      });

      observer.observe(rootRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [braze, card, viewUuid, isSent]);

  if (!braze) return null;

  if (card instanceof braze.ControlCard) {
    return <div ref={rootRef} />;
  }

  if (
    !(card instanceof braze.ClassicCard) &&
    !(card instanceof braze.CaptionedImage)
  ) {
    return null;
  }

  const headingId = sanitizeHtmlId(`content-card-${card.id}-heading`);

  return (
    <div
      className={styles.root}
      role="group"
      aria-labelledby={headingId}
      ref={rootRef}
    >
      <Container>
        <GridContainer>
          <GridElement mobile={0} desktopSmall={1} />

          <GridElement
            mobile={12}
            mobileLandscape={6}
            orderMobileLandscape={2}
            desktopSmall={11}
          >
            <div className={styles.imageContainer}>
              {card.imageUrl && (
                <img
                  alt={card.altImageText || ''}
                  className={styles.image}
                  loading="lazy"
                  src={card.imageUrl}
                />
              )}
            </div>
          </GridElement>

          <GridElement mobile={12} mobileLandscape={6} desktopSmall={10}>
            <div className={styles.textContent}>
              <h2 className={styles.title} id={headingId}>
                {card.title}
              </h2>

              <p className={styles.description}>{card.description}</p>

              {card.linkText && card.url ? (
                <div className={styles.buttonContainer}>
                  <Button
                    onClick={() => {
                      braze.logContentCardClick(card);
                      trackCardClick?.();
                    }}
                    pointer={{ uri: card.url }}
                    size="sm"
                    text={card.linkText}
                    variant="primary"
                  />
                </div>
              ) : null}
            </div>
          </GridElement>

          <GridElement mobile={0} desktopSmall={1} />
        </GridContainer>
      </Container>
    </div>
  );
};

export function getContentCardForSlot(
  slot: ContentCardSlot,
  contentArray: Content[],
  contentCards: BrazeContentCardType[]
): BrazeContentCardType | null {
  const availableContentCardsForPlacement = contentCards.filter(
    (c) => getContentCardPlacementId(c) === slot.placementId
  );

  const nthSlotForPlacement = contentArray
    .filter(
      (c) => c.type === 'contentCard' && c.placementId === slot.placementId
    )
    .indexOf(slot);

  return availableContentCardsForPlacement[nthSlotForPlacement] || null;
}

function getContentCardPlacementId(
  contentCard: BrazeContentCardType
): string | undefined {
  return contentCard.extras['placementId'];
}
